import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import MotiveRadar from "@/components/MotiveRadar";
import PlayerCard from "@/components/PlayerCard";
import { getMotiveIcon, scoreBand } from "@/lib/motives";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Download, Home, UserPlus } from "lucide-react";
import { Link } from "react-router-dom";

export default function DemoResult() {
  const { demoId } = useParams();
  const [data, setData] = useState(null);
  const [motives, setMotives] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [r, m] = await Promise.all([
          api.get(`/demo/${demoId}`),
          api.get("/meta/motives"),
        ]);
        setData(r.data);
        setMotives(m.data.motives);
      } catch (e) {
        setErr(formatApiError(e));
      }
    })();
  }, [demoId]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/demo/${demoId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `FMP_Demo_${data.name.replace(/ /g, "_")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  if (err)
    return (
      <div className="mx-auto max-w-4xl px-6 py-16 text-center text-red-400">
        {err}
      </div>
    );
  if (!data)
    return (
      <div className="mx-auto max-w-4xl px-6 py-16 text-center text-white/40">
        Wird geladen …
      </div>
    );

  const { name, analysis } = data;
  const { archetype, top_motives, bottom_motives, scores, position_fit } = analysis;

  return (
    <div className="mx-auto max-w-6xl px-6 py-12 space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
            / DEMO-ERGEBNIS
          </div>
          <div className="font-heading font-black text-3xl tracking-tight">
            Dein FMP-Profil
          </div>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="demo-pdf-btn"
            onClick={downloadPdf}
            disabled={downloading}
            className="btn-primary"
          >
            <Download className="w-5 h-5" />
            {downloading ? "PDF wird erstellt …" : "PDF herunterladen"}
          </button>
        </div>
      </div>

      <PlayerCard archetype={archetype} player={{ name }} teamName="Demo-Profil" />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-elevated p-6 md:p-8">
          <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
            / MOTIV-RADAR
          </div>
          <h3 className="font-heading font-black text-2xl mb-4">12 Motive</h3>
          <MotiveRadar scores={scores} motives={motives} />
        </div>

        <div className="space-y-4">
          <div className="card-elevated p-6 md:p-8">
            <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
              / STÄRKEN
            </div>
            <div className="space-y-2">
              {top_motives.map((m) => {
                const Icon = getMotiveIcon(m.icon);
                return <MotiveRow key={m.slug} m={m} Icon={Icon} />;
              })}
            </div>
          </div>
          <div className="card-elevated p-6 md:p-8">
            <div className="font-display text-xs tracking-[0.3em] text-white/40 mb-3">
              / ENTWICKLUNGS­FELDER
            </div>
            <div className="space-y-2">
              {bottom_motives.map((m) => {
                const Icon = getMotiveIcon(m.icon);
                return <MotiveRow key={m.slug} m={m} Icon={Icon} muted />;
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Trainerhinweise */}
      <div className="card-elevated p-6 md:p-8">
        <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
          / TRAINERHINWEISE
        </div>
        <h3 className="font-heading font-black text-2xl mb-6">
          Empfehlungen für {archetype.name}
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          {archetype.trainer_hints.map((hint, i) => (
            <div
              key={i}
              className="p-5 border border-white/10 bg-[#0f1013] rounded-md flex gap-3"
            >
              <div className="font-heading font-black text-3xl text-[#CCFF00]/70 leading-none">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="text-sm text-white/80 leading-relaxed pt-1">{hint}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="card-elevated p-6 md:p-8 border-[#CCFF00]/30 border-2">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-heading font-bold text-xl mb-1">
              Trainer? Baue jetzt dein Team auf.
            </div>
            <div className="text-white/60 text-sm">
              Kostenloses Trainer-Konto anlegen und deine gesamte Mannschaft in 10 Minuten testen.
            </div>
          </div>
          <Link to="/register" data-testid="demo-cta-register" className="btn-primary">
            <UserPlus className="w-5 h-5" />
            Trainer-Konto anlegen
          </Link>
        </div>
      </div>
    </div>
  );
}

function MotiveRow({ m, Icon, muted }) {
  const band = scoreBand(m.score);
  return (
    <div className="flex items-center gap-3 p-2">
      <div
        className="w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0"
        style={{
          background: muted ? "rgba(255,255,255,0.05)" : `${m.color}22`,
          border: `1px solid ${muted ? "rgba(255,255,255,0.1)" : m.color + "66"}`,
          color: muted ? "#94A3B8" : m.color,
        }}
      >
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-sm">{m.name}</div>
        <div
          className="font-display text-[10px] tracking-widest"
          style={{ color: band.color }}
        >
          {band.label}
        </div>
      </div>
      <div className="font-heading font-black text-xl">{m.score}</div>
    </div>
  );
}
